import { z } from "zod";

export const syntheticFixtureIdSchema = z.enum([
  "small-complete",
  "archive-heavy",
  "custom-fields",
  "checklist-heavy",
  "attachment-mix",
  "attachment-limits",
  "pagination-large",
  "permissions",
  "partial-failure",
  "restore-mapping",
]);
export type SyntheticFixtureId = z.infer<typeof syntheticFixtureIdSchema>;

export const syntheticFixtureTraitSchema = z.enum([
  "unicode_emoji",
  "long_text",
  "archived_objects",
  "custom_field_text",
  "custom_field_number",
  "custom_field_date",
  "custom_field_checkbox",
  "custom_field_list",
  "checklists",
  "labels",
  "dates",
  "members",
  "comments",
  "attachments",
  "pagination",
  "revoked_token",
  "expired_token",
  "under_scoped_token",
  "rate_limit",
  "d1_failure",
  "r2_failure",
  "queue_retry",
  "daylight_saving",
  "retention",
  "account_deletion",
  "restore_resume",
  "missing_members",
  "duplicate_override",
]);
export type SyntheticFixtureTrait = z.infer<typeof syntheticFixtureTraitSchema>;

export const syntheticAttachmentCaseSchema = z.enum([
  "trello_upload",
  "safe_https",
  "protected_drive",
  "protected_dropbox",
  "protected_onedrive",
  "missing",
  "duplicate_filename",
  "unicode_filename",
  "boundary_size",
  "oversized",
  "redirect",
  "timeout",
  "unsupported_scheme",
]);
export type SyntheticAttachmentCase = z.infer<
  typeof syntheticAttachmentCaseSchema
>;

const fixtureScaleSchema = z
  .object({
    actions: z.int().nonnegative(),
    attachments: z.int().nonnegative(),
    cards: z.int().nonnegative(),
    checkItems: z.int().nonnegative(),
    checklists: z.int().nonnegative(),
    customFields: z.int().nonnegative(),
    lists: z.int().nonnegative(),
    members: z.int().nonnegative(),
  })
  .strict();

const syntheticFixtureSchema = z
  .object({
    attachmentCases: z.array(syntheticAttachmentCaseSchema),
    expectedOutcomes: z
      .array(z.enum(["complete", "complete_with_warnings", "failed"]))
      .min(1),
    id: syntheticFixtureIdSchema,
    purpose: z.string().min(1).max(300),
    scale: fixtureScaleSchema,
    status: z.enum(["specified", "implemented"]),
    traits: z.array(syntheticFixtureTraitSchema).min(1),
  })
  .strict()
  .superRefine((fixture, context) => {
    for (const [label, values] of [
      ["attachmentCases", fixture.attachmentCases],
      ["expectedOutcomes", fixture.expectedOutcomes],
      ["traits", fixture.traits],
    ] as const) {
      if (new Set(values).size !== values.length) {
        context.addIssue({
          code: "custom",
          message: `${label} must not contain duplicates.`,
          path: [label],
        });
      }
    }
  });

const REQUIRED_FIXTURE_IDS = syntheticFixtureIdSchema.options;

export const syntheticFixtureCatalogSchema = z
  .object({
    fixtures: z.array(syntheticFixtureSchema),
    provenance: z.literal("generated-only"),
    schemaVersion: z.literal(1),
    synthetic: z.literal(true),
  })
  .strict()
  .superRefine((catalog, context) => {
    const ids = catalog.fixtures.map((fixture) => fixture.id);

    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: "custom",
        message: "Fixture IDs must be unique.",
        path: ["fixtures"],
      });
    }

    const missingIds = REQUIRED_FIXTURE_IDS.filter((id) => !ids.includes(id));
    const unexpectedCount = ids.length - REQUIRED_FIXTURE_IDS.length;

    if (missingIds.length > 0 || unexpectedCount !== 0) {
      context.addIssue({
        code: "custom",
        message: `Catalog must contain every required fixture exactly once. Missing: ${
          missingIds.join(", ") || "none"
        }.`,
        path: ["fixtures"],
      });
    }
  });

export type SyntheticFixtureCatalog = z.infer<
  typeof syntheticFixtureCatalogSchema
>;

const trelloIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/, "Expected a synthetic 24-character Trello ID.");

const trelloMemberSchema = z
  .object({
    fullName: z.string().min(1),
    id: trelloIdSchema,
    username: z.string().min(1),
  })
  .strict();

const trelloListSchema = z
  .object({
    closed: z.boolean(),
    id: trelloIdSchema,
    idBoard: trelloIdSchema,
    name: z.string().min(1),
    pos: z.number().nonnegative(),
  })
  .strict();

const trelloLabelSchema = z
  .object({
    color: z
      .enum([
        "black",
        "blue",
        "green",
        "lime",
        "orange",
        "pink",
        "purple",
        "red",
        "sky",
        "yellow",
      ])
      .nullable(),
    id: trelloIdSchema,
    idBoard: trelloIdSchema,
    name: z.string(),
  })
  .strict();

const trelloCardSchema = z
  .object({
    closed: z.boolean(),
    desc: z.string(),
    due: z.iso.datetime().nullable(),
    dueComplete: z.boolean(),
    id: trelloIdSchema,
    idBoard: trelloIdSchema,
    idChecklists: z.array(trelloIdSchema),
    idLabels: z.array(trelloIdSchema),
    idList: trelloIdSchema,
    idMembers: z.array(trelloIdSchema),
    name: z.string().min(1),
    pos: z.number().nonnegative(),
    start: z.iso.datetime().nullable(),
  })
  .strict();

const trelloCheckItemSchema = z
  .object({
    due: z.iso.datetime().nullable(),
    id: trelloIdSchema,
    idMember: trelloIdSchema.nullable(),
    name: z.string().min(1),
    pos: z.number().nonnegative(),
    state: z.enum(["complete", "incomplete"]),
  })
  .strict();

const trelloChecklistSchema = z
  .object({
    checkItems: z.array(trelloCheckItemSchema),
    id: trelloIdSchema,
    idBoard: trelloIdSchema,
    idCard: trelloIdSchema,
    name: z.string().min(1),
    pos: z.number().nonnegative(),
  })
  .strict();

const trelloCommentActionSchema = z
  .object({
    data: z
      .object({
        board: z
          .object({
            id: trelloIdSchema,
            name: z.string().min(1),
          })
          .strict(),
        card: z
          .object({
            id: trelloIdSchema,
            name: z.string().min(1),
          })
          .strict(),
        text: z.string().min(1),
      })
      .strict(),
    date: z.iso.datetime(),
    id: trelloIdSchema,
    idMemberCreator: trelloIdSchema,
    memberCreator: trelloMemberSchema,
    type: z.literal("commentCard"),
  })
  .strict();

const expectedCaptureCountsSchema = z
  .object({
    actions: z.int().nonnegative(),
    cards: z.int().nonnegative(),
    checkItems: z.int().nonnegative(),
    checklists: z.int().nonnegative(),
    labels: z.int().nonnegative(),
    lists: z.int().nonnegative(),
    members: z.int().nonnegative(),
  })
  .strict();

export const syntheticTrelloCaptureFixtureSchema = z
  .object({
    actions: z.array(trelloCommentActionSchema),
    board: z
      .object({
        closed: z.boolean(),
        desc: z.string(),
        id: trelloIdSchema,
        idOrganization: trelloIdSchema.nullable(),
        name: z.string().min(1),
        prefs: z
          .object({
            cardCovers: z.boolean(),
            permissionLevel: z.enum(["org", "private", "public"]),
          })
          .strict(),
      })
      .strict(),
    cards: z.array(trelloCardSchema),
    checklists: z.array(trelloChecklistSchema),
    expected: z
      .object({
        counts: expectedCaptureCountsSchema,
        outcome: z.literal("complete"),
        warningCodes: z.array(z.string()).length(0),
      })
      .strict(),
    fixtureId: z.literal("small-complete"),
    labels: z.array(trelloLabelSchema),
    lists: z.array(trelloListSchema),
    members: z.array(trelloMemberSchema),
    provenance: z.literal("generated-only"),
    schemaVersion: z.literal(1),
    synthetic: z.literal(true),
  })
  .strict();

export type SyntheticTrelloCaptureFixture = z.infer<
  typeof syntheticTrelloCaptureFixtureSchema
>;

const trelloCustomFieldTypeSchema = z.enum([
  "checkbox",
  "date",
  "list",
  "number",
  "text",
]);

const trelloCustomFieldOptionSchema = z
  .object({
    color: z.enum([
      "black",
      "blue",
      "green",
      "lime",
      "none",
      "orange",
      "pink",
      "purple",
      "red",
      "sky",
      "yellow",
    ]),
    id: trelloIdSchema,
    idCustomField: trelloIdSchema,
    pos: z.number().nonnegative(),
    value: z.object({ text: z.string().min(1) }).strict(),
  })
  .strict();

const trelloCustomFieldDefinitionSchema = z
  .object({
    fieldGroup: z.string().regex(/^[a-f0-9]{64}$/),
    id: trelloIdSchema,
    idModel: trelloIdSchema,
    modelType: z.literal("board"),
    name: z.string().min(1),
    options: z.array(trelloCustomFieldOptionSchema).optional(),
    pos: z.number().nonnegative(),
    type: trelloCustomFieldTypeSchema,
  })
  .strict()
  .superRefine((field, context) => {
    if (field.type === "list" && !field.options?.length) {
      context.addIssue({
        code: "custom",
        message: "List custom fields require at least one option.",
        path: ["options"],
      });
    }

    if (field.type !== "list" && field.options !== undefined) {
      context.addIssue({
        code: "custom",
        message: "Only list custom fields may define options.",
        path: ["options"],
      });
    }

    for (const option of field.options ?? []) {
      if (option.idCustomField !== field.id) {
        context.addIssue({
          code: "custom",
          message: "Option must reference its containing custom field.",
          path: ["options"],
        });
      }
    }
  });

const trelloCustomFieldValueSchema = z.union([
  z.object({ checked: z.enum(["true", "false"]) }).strict(),
  z.object({ date: z.iso.datetime() }).strict(),
  z.object({ number: z.string().regex(/^-?\d+(?:\.\d+)?$/) }).strict(),
  z.object({ text: z.string() }).strict(),
]);

const trelloCustomFieldItemSchema = z
  .object({
    id: trelloIdSchema,
    idCustomField: trelloIdSchema,
    idModel: trelloIdSchema,
    idValue: trelloIdSchema.optional(),
    modelType: z.literal("card"),
    value: trelloCustomFieldValueSchema.optional(),
  })
  .strict()
  .superRefine((item, context) => {
    if ((item.idValue === undefined) === (item.value === undefined)) {
      context.addIssue({
        code: "custom",
        message: "A custom-field item must contain exactly one value form.",
      });
    }
  });

export const syntheticTrelloCustomFieldsFixtureSchema = z
  .object({
    board: z
      .object({
        id: trelloIdSchema,
        name: z.string().min(1),
      })
      .strict(),
    cards: z.array(
      z
        .object({
          customFieldItems: z.array(trelloCustomFieldItemSchema),
          id: trelloIdSchema,
          idBoard: trelloIdSchema,
          name: z.string().min(1),
        })
        .strict(),
    ),
    customFields: z.array(trelloCustomFieldDefinitionSchema),
    expected: z
      .object({
        counts: z
          .object({
            cards: z.int().nonnegative(),
            customFieldItems: z.int().nonnegative(),
            customFields: z.int().nonnegative(),
          })
          .strict(),
        outcome: z.literal("complete"),
        warningCodes: z.array(z.string()).length(0),
      })
      .strict(),
    fixtureId: z.literal("custom-fields"),
    provenance: z.literal("generated-only"),
    schemaVersion: z.literal(1),
    synthetic: z.literal(true),
  })
  .strict()
  .superRefine((fixture, context) => {
    const fieldsById = new Map(
      fixture.customFields.map((field) => [field.id, field]),
    );
    const expectedValueKeyByType = {
      checkbox: "checked",
      date: "date",
      number: "number",
      text: "text",
    } as const;
    const itemIds = new Set<string>();
    let customFieldItemCount = 0;

    fixture.customFields.forEach((field, fieldIndex) => {
      if (field.idModel !== fixture.board.id) {
        context.addIssue({
          code: "custom",
          message: "Custom field must reference the fixture board.",
          path: ["customFields", fieldIndex, "idModel"],
        });
      }
    });

    fixture.cards.forEach((card, cardIndex) => {
      if (card.idBoard !== fixture.board.id) {
        context.addIssue({
          code: "custom",
          message: "Card must reference the fixture board.",
          path: ["cards", cardIndex, "idBoard"],
        });
      }

      card.customFieldItems.forEach((item, itemIndex) => {
        customFieldItemCount += 1;
        const path = ["cards", cardIndex, "customFieldItems", itemIndex];
        const field = fieldsById.get(item.idCustomField);

        if (itemIds.has(item.id)) {
          context.addIssue({
            code: "custom",
            message: "Custom-field item IDs must be unique.",
            path: [...path, "id"],
          });
        }
        itemIds.add(item.id);

        if (item.idModel !== card.id) {
          context.addIssue({
            code: "custom",
            message: "Custom-field item must reference its containing card.",
            path: [...path, "idModel"],
          });
        }

        if (field === undefined) {
          context.addIssue({
            code: "custom",
            message: "Custom-field item references an unknown definition.",
            path: [...path, "idCustomField"],
          });
          return;
        }

        if (field.type === "list") {
          if (
            !field.options?.some(({ id }) => id === item.idValue) ||
            item.value !== undefined
          ) {
            context.addIssue({
              code: "custom",
              message:
                "List item must reference an option from its definition.",
              path,
            });
          }
          return;
        }

        const actualValueKey = Object.keys(item.value ?? {})[0];
        if (
          item.idValue !== undefined ||
          actualValueKey !== expectedValueKeyByType[field.type]
        ) {
          context.addIssue({
            code: "custom",
            message: `Item value does not match ${field.type} definition.`,
            path,
          });
        }
      });
    });

    const actualCounts = {
      cards: fixture.cards.length,
      customFieldItems: customFieldItemCount,
      customFields: fixture.customFields.length,
    };

    if (
      Object.entries(actualCounts).some(
        ([key, count]) =>
          count !==
          fixture.expected.counts[key as keyof typeof fixture.expected.counts],
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Declared fixture counts must match fixture content.",
        path: ["expected", "counts"],
      });
    }
  });

export type SyntheticTrelloCustomFieldsFixture = z.infer<
  typeof syntheticTrelloCustomFieldsFixtureSchema
>;
